import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Play } from "lucide-react";
import Navbar from "../components/shared/Navbar";
import { Reveal } from "../components/Reveal";
import GigCard from "../components/marketplace/GigCard";
import SpaceBackground from "../components/SpaceBackground";
import BrandMark from "../components/BrandMark";
import "../home.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { key: "tiktok", label: "TikTok", desc: "Go viral with real creators pushing your sound.", brand: "tiktok", to: "/marketplace?platform=tiktok" },
  { key: "spotify", label: "Spotify", desc: "Curated playlist placements that convert to fans.", brand: "spotify", to: "/marketplace?platform=spotify" },
  { key: "meta", label: "Instagram & Meta", desc: "Reels and feed reach across the Meta network.", brand: "meta", to: "/marketplace?platform=meta" },
];

const TIKTOKS = [
  { id: "7647404915362499854", title: "Brazil Funk" },
  { id: "7497366833213869334", title: "Dance Phonk" },
  { id: "7556700302678773014", title: "Car Edit" },
];

// The TikTok embed (and its cookies) only loads after the visitor presses play.
// Add `src: "/videos/file.mp4"` to an entry to play a self-hosted file instead of the TikTok embed.
const TikTokCard = ({ id, title, src }) => {
  const [playing, setPlaying] = useState(false);
  if (src) {
    return (
      <div className="hx-tiktok">
        <video src={src} controls playsInline preload="metadata" title={title} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className="hx-tiktok">
      {playing ? (
        <iframe
          src={`https://www.tiktok.com/embed/${id}`}
          allow="autoplay; encrypted-media"
          allowFullScreen
          title={`TikTok video - ${title}`}
        />
      ) : (
        <button type="button" className="hx-tiktok-poster" onClick={() => setPlaying(true)} aria-label={`Play ${title}`}>
          <span className="hx-tiktok-play"><Play className="w-6 h-6" /></span>
          <span className="hx-mono">TikTok</span>
          <span className="hx-tiktok-title">{title}</span>
        </button>
      )}
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredGigs, setFeaturedGigs] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/showcase-videos`)
      .then((res) => setUploadedVideos(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/gigs`, { params: { sort_by: "popular", limit: 4 } })
      .then((res) => {
        if (!active) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setFeaturedGigs(data.slice(0, 4));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden relative">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="hero-section space-zone relative overflow-hidden" data-testid="hero-section">
        <SpaceBackground contained />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 md:px-12 lg:px-24 w-full pt-32 pb-24 md:pt-40 md:pb-32">
          <div className="max-w-[720px]">
            <h1 className="hx-headline word-reveal" style={{ animationDelay: "0.15s" }}>
              Music promotion, <em>simplified.</em>
            </h1>

            <p className="mt-7 text-base sm:text-lg max-w-[34rem] leading-relaxed word-reveal" style={{ animationDelay: "0.3s", color: "#a1a1aa" }}>
              Connect with verified promoters and launch campaigns across TikTok, Meta and Spotify. Real reach, real results, no bots.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 word-reveal" style={{ animationDelay: "0.45s" }}>
              <Link to="/register" data-testid="hero-cta-primary" className="hx-btn hx-btn--solid group">
                Start promoting
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/marketplace" data-testid="hero-cta-secondary" className="hx-btn hx-btn--ghost">
                <Play className="w-4 h-4" /> Explore marketplace
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* ===== PLATFORMS ===== */}
      <section className="hx-section" data-testid="browse-platform-section">
        <div className="hx-wrap">
          <Reveal className="mb-10">
            <span className="hx-mono hx-faint">Browse by platform</span>
            <h2 className="hx-h2 mt-3">Where do you want to blow up?</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLATFORMS.map((p, i) => (
              <Reveal key={p.key} delay={i * 90}>
                <Link to={p.to} data-testid={`platform-card-${p.key}`} className="hx-card">
                  <span className="hx-index hx-mono">0{i + 1}</span>
                  <div className="hx-icon mb-8"><BrandMark brand={p.brand} className="w-5 h-5" /></div>
                  <h3 className="hx-title text-xl font-bold mb-2">{p.label}</h3>
                  <p className="hx-muted text-sm leading-relaxed">{p.desc}</p>
                  <span className="hx-link">
                    Browse gigs <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TIKTOK ===== */}
      <section className="hx-section" data-testid="tiktok-videos-section">
        <div className="hx-wrap">
          <Reveal className="mb-14 text-center">
            <span className="hx-mono hx-faint">Showcase</span>
            <h2 className="hx-h2 mt-3">Featured on TikTok</h2>
            <p className="hx-muted mt-3">Watch trending tracks and campaigns from our platform</p>
          </Reveal>
          <div className="hx-tiktok-row">
            {(uploadedVideos.length
              ? uploadedVideos.map((v, i) => ({ id: v.id, title: v.title || `Video ${i + 1}`, src: `${API_URL}${v.video_url}` }))
              : TIKTOKS
            ).map((t, i) => (
              <Reveal key={t.id} delay={i * 80}>
                <TikTokCard id={t.id} title={t.title} src={t.src} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED GIGS ===== */}
      {featuredGigs.length > 0 && (
        <section className="hx-section" data-testid="featured-gigs-section">
          <div className="hx-wrap">
            <Reveal className="flex items-end justify-between gap-6 mb-10">
              <h2 className="hx-h2">Trending gigs</h2>
              <Link to="/marketplace" className="hx-link !mt-0 !pt-0 shrink-0" data-testid="featured-gigs-browse-all">
                Browse all <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {featuredGigs.map((gig, index) => (
                <Reveal key={gig.id} delay={index * 80}>
                  <GigCard gig={gig} index={index} navigate={navigate} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="hx-footer" data-testid="footer">
        <div className="hx-wrap">
          <div className="grid gap-6 lg:grid-cols-[1fr_minmax(0,2fr)_auto] lg:items-start">
            <div className="min-w-0 whitespace-nowrap font-['Syne'] text-[1.5rem] font-extrabold tracking-[-0.06em] text-white">
              DOPE MEDIA
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-zinc-300">
              <Link to="/marketplace" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Marketplace</Link>
              <Link to="/faq" className="transition-colors hover:text-zinc-900 dark:hover:text-white">FAQ</Link>
              <Link to="/prijzen" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Prijzen</Link>
              <Link to="/terms" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Terms</Link>
              <Link to="/privacy" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Privacy</Link>
              <Link to="/cookies" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Cookies</Link>
              <Link to="/distributieovereenkomst" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Distributieovereenkomst</Link>
              <Link to="/platform-rules" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Platformregels</Link>
              <Link to="/report-abuse" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Meld misbruik</Link>
              <Link to="/contact" className="transition-colors hover:text-zinc-900 dark:hover:text-white">Contact</Link>
            </div>

            <div className="text-sm text-zinc-600 dark:text-zinc-400 lg:text-right">
              © 2026 DOPE MEDIA. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
